import { getStateMultiplier, getStateName } from "@/lib/states";

// NOTE ON SCHEMA DIVERGENCE (2026-02 product decision):
// The per-calculator inline ranges + QUALITY_MULTIPLIERS in THIS file use a
// DIFFERENT scope schema than the public API at /app/backend/public_api.py
// (which reads /app/shared/cost-data.json). Example: bathroom here ships
// scopes {full, full_layout, fixtures, flooring} at $1.5K-$32K for UI-flow
// granularity, while the public API ships {full, primary-suite, powder-room}
// at $14K-$95K for whole-bathroom aggregator/GPT consumers. INTENTIONAL —
// do not naively dedupe. A real unification would require product-level
// scope-schema decisions; see public_api.py docstring for the same note.

// Quality multipliers (Budget x1.0, Mid x1.3, High x2.2)
export const QUALITY_MULTIPLIERS = {
  budget: 1.0,
  mid: 1.3,
  high: 2.2,
};

// Cost split applied to "subtotal" (labor 50%, materials 35%, permits 5%)
// Contingency 10% is added on top of the subtotal.
const COST_SPLIT = { labor: 0.5, materials: 0.35, permits: 0.05 };
const CONTINGENCY = 0.1;

/* Each calculator config has steps and a base ranges resolver. */

const BATHROOM = {
  slug: "bathroom",
  path: "/bathroom-remodel-cost-calculator",
  statePathBase: "/bathroom-remodel-cost",
  name: "Bathroom Remodel",
  shortName: "Bathroom",
  description: "Estimate the cost of remodeling your bathroom — fixtures, flooring, or full renovation.",
  image: "https://images.pexels.com/photos/8146201/pexels-photo-8146201.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  icon: "bath",
  timeline: "Most bathroom remodels take 1–3 weeks from demo to final inspection.",
  tips: [
    "Keep the existing plumbing layout — moving pipes can add $1,000–$5,000.",
    "Choose mid-range fixtures with high-end finishes; the difference is rarely visible.",
    "Buy materials yourself (tile, vanity, mirror) to avoid contractor markup of 15–25%.",
  ],
  seo: {
    title: "Bathroom Remodel Cost Calculator | HavenCostGuide",
    description: "Free bathroom remodel cost calculator. Get a state-specific estimate for fixtures, flooring, and full renovations in seconds.",
  },
  faqs: [
    {
      q: "How much does a bathroom remodel cost in 2026?",
      a: "A typical full bathroom remodel runs $7,000–$15,000 for a medium-sized bathroom at mid-range quality. Costs are 40% higher in California and New York and roughly 15% lower in most Midwest states. High-end remodels with premium tile, custom vanities, and frameless glass can reach $25,000–$55,000.",
    },
    {
      q: "Do I need a permit for a bathroom remodel?",
      a: "Cosmetic updates (paint, fixtures, mirrors) usually don't need a permit. You will need a permit for plumbing relocations, electrical changes, or moving walls. Always check with your local building department — most U.S. cities require permits for any work involving plumbing or electrical changes.",
    },
    {
      q: "How long does a bathroom remodel take?",
      a: "Most bathroom remodels take 1–3 weeks from demo to final inspection. Layout changes and custom tile work add 1–2 weeks. Fixture-only swaps typically take 2–4 days.",
    },
    {
      q: "What's the most cost-effective way to update a bathroom?",
      a: "Refacing the vanity, swapping fixtures, regrouting tile, and replacing the toilet typically delivers the biggest visual improvement per dollar. A budget-grade refresh starts around $1,500–$3,500.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Project Scope",
      question: "What are you remodeling?",
      options: [
        { value: "full", label: "Full bathroom remodel", hint: "Replace fixtures, finishes, and surfaces" },
        { value: "fixtures", label: "Fixtures only", hint: "Toilet, sink, and tub" },
        { value: "flooring", label: "Flooring only", hint: "Tile, vinyl, or other flooring" },
        { value: "full_layout", label: "Full remodel + layout changes", hint: "Move plumbing or walls" },
      ],
    },
    {
      key: "size",
      title: "Bathroom Size",
      question: "How big is your bathroom?",
      options: [
        { value: "small", label: "Small", hint: "Under 50 sq ft" },
        { value: "medium", label: "Medium", hint: "50–100 sq ft" },
        { value: "large", label: "Large", hint: "Over 100 sq ft" },
      ],
    },
    {
      key: "quality",
      title: "Quality Level",
      question: "What quality level are you aiming for?",
      options: [
        { value: "budget", label: "Budget", hint: "Builder grade" },
        { value: "mid", label: "Mid-range", hint: "Standard upgrades" },
        { value: "high", label: "High-end", hint: "Premium materials" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      full: { small: [4000, 8000], medium: [7000, 15000], large: [12000, 25000] },
      full_layout: { small: [5500, 10500], medium: [9500, 19500], large: [16000, 32500] },
      fixtures: { small: [1500, 3500], medium: [2500, 5500], large: [3500, 7500] },
      flooring: { small: [800, 2000], medium: [1500, 3500], large: [2500, 5000] },
    };
    return map[scope]?.[size] || null;
  },
};

const KITCHEN = {
  slug: "kitchen",
  path: "/kitchen-remodel-cost-calculator",
  statePathBase: "/kitchen-remodel-cost",
  name: "Kitchen Remodel",
  shortName: "Kitchen",
  description: "Estimate cabinets, countertops, appliances, or a full kitchen renovation.",
  image: "https://images.pexels.com/photos/8082304/pexels-photo-8082304.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  icon: "chef-hat",
  timeline: "Most kitchen remodels take 4–8 weeks, longer with layout changes.",
  tips: [
    "Refacing cabinets instead of replacing can save 40–60% on cabinetry.",
    "Quartz often costs less than premium granite and needs no sealing.",
    "Get at least three contractor bids — kitchen pricing varies widely by region.",
  ],
  seo: {
    title: "Kitchen Remodel Cost Calculator | HavenCostGuide",
    description: "Free kitchen remodel cost calculator with state-specific pricing for cabinets, countertops, and full renovations.",
  },
  faqs: [
    {
      q: "How much does a kitchen remodel cost?",
      a: "A typical mid-range kitchen remodel costs $20,000–$45,000 for a medium-sized kitchen. Cabinets-only refreshes start at $6,000–$15,000. High-end remodels with custom cabinetry and premium stone can exceed $75,000–$95,000.",
    },
    {
      q: "What's the most expensive part of a kitchen remodel?",
      a: "Cabinets typically account for 30–35% of the total budget, followed by labor (about 25–30%). Counter-tops, appliances, and electrical/plumbing each take 8–15%.",
    },
    {
      q: "How long does a kitchen remodel take?",
      a: "Most kitchen remodels run 4–8 weeks. Cabinets-only or counter-only projects can finish in 1–3 weeks. Layout changes that involve moving plumbing, gas, or load-bearing walls usually add 2–4 weeks.",
    },
    {
      q: "Are kitchen remodels worth the investment?",
      a: "Mid-range kitchen remodels recoup 60–75% of their cost at resale according to Remodeling Magazine's Cost vs. Value report. Minor refreshes (paint, fixtures, hardware) typically have the highest ROI.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Project Scope",
      question: "What are you remodeling?",
      options: [
        { value: "full", label: "Full kitchen remodel", hint: "Cabinets, counters, appliances, finishes" },
        { value: "cabinets", label: "Cabinets only", hint: "Replace or reface cabinetry" },
        { value: "countertops", label: "Countertops only", hint: "Replace counters and backsplash" },
        { value: "full_layout", label: "Full remodel + layout changes", hint: "Move walls, plumbing, or gas lines" },
      ],
    },
    {
      key: "size",
      title: "Kitchen Size",
      question: "How big is your kitchen?",
      options: [
        { value: "small", label: "Small", hint: "Under 100 sq ft" },
        { value: "medium", label: "Medium", hint: "100–200 sq ft" },
        { value: "large", label: "Large", hint: "Over 200 sq ft" },
      ],
    },
    {
      key: "quality",
      title: "Quality Level",
      question: "What quality level are you aiming for?",
      options: [
        { value: "budget", label: "Budget", hint: "Stock cabinets, laminate" },
        { value: "mid", label: "Mid-range", hint: "Semi-custom, quartz" },
        { value: "high", label: "High-end", hint: "Custom cabinetry, premium stone" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      full: { small: [10000, 25000], medium: [20000, 45000], large: [35000, 75000] },
      full_layout: { small: [14000, 32000], medium: [26000, 58000], large: [45000, 95000] },
      cabinets: { small: [3000, 8000], medium: [6000, 15000], large: [10000, 25000] },
      countertops: { small: [1500, 4000], medium: [2500, 6500], large: [4000, 10000] },
    };
    return map[scope]?.[size] || null;
  },
};

const FLOORING = {
  slug: "flooring",
  path: "/flooring-cost-calculator",
  statePathBase: "/flooring-cost",
  name: "Flooring Installation",
  shortName: "Flooring",
  description: "Estimate hardwood, tile, vinyl, laminate, or carpet installation costs.",
  image: "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?auto=format&fit=crop&w=940&q=80",
  icon: "layers",
  timeline: "Most flooring jobs take 2–7 days depending on size and material.",
  tips: [
    "Engineered hardwood looks like solid wood at 30–50% lower cost.",
    "Removing old flooring yourself can save $1–$3 per square foot.",
    "Order 10% extra for cuts, waste, and future repairs.",
  ],
  seo: {
    title: "Flooring Cost Calculator | HavenCostGuide",
    description: "Free flooring installation cost calculator. State-specific estimates for hardwood, tile, vinyl, laminate, and carpet.",
  },
  faqs: [
    {
      q: "How much does new flooring cost per square foot?",
      a: "Installed costs typically run: laminate $3–$7/sq ft, vinyl plank $4–$9/sq ft, tile $7–$15/sq ft, engineered hardwood $8–$15/sq ft, and solid hardwood $10–$20/sq ft. Premium materials and custom patterns push the high end.",
    },
    {
      q: "What's the cheapest flooring to install?",
      a: "Laminate and vinyl plank are the most affordable options at $3–$9/sq ft installed. Both are durable, easy to clean, and click-lock systems can be DIY-installed to save another 30–50%.",
    },
    {
      q: "How long does flooring installation take?",
      a: "Most jobs take 2–7 days. Vinyl and laminate go fastest (1–3 days). Tile and hardwood take longer due to subfloor prep, drying, and curing — usually 4–7 days for an average home.",
    },
    {
      q: "Should I buy materials myself or let the contractor source them?",
      a: "Buying materials yourself can save 15–25% on contractor markup, but you take on responsibility for ordering the right quantity (always add 10% for waste) and for material defects.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Flooring Type",
      question: "Which type of flooring?",
      options: [
        { value: "hardwood", label: "Hardwood", hint: "Solid or engineered wood" },
        { value: "tile", label: "Tile", hint: "Ceramic or porcelain" },
        { value: "vinyl", label: "Vinyl / LVP", hint: "Luxury vinyl plank" },
        { value: "laminate", label: "Laminate", hint: "Click-lock laminate" },
        { value: "carpet", label: "Carpet", hint: "Wall-to-wall carpet" },
      ],
    },
    {
      key: "size",
      title: "Area Size",
      question: "How much area are you covering?",
      options: [
        { value: "small", label: "Small", hint: "Under 500 sq ft" },
        { value: "medium", label: "Medium", hint: "500–1,500 sq ft" },
        { value: "large", label: "Large", hint: "Over 1,500 sq ft" },
      ],
    },
    {
      key: "quality",
      title: "Quality Level",
      question: "What quality level are you aiming for?",
      options: [
        { value: "budget", label: "Budget", hint: "Entry-level materials" },
        { value: "mid", label: "Mid-range", hint: "Standard quality" },
        { value: "high", label: "High-end", hint: "Premium products" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      hardwood: { small: [2500, 5500], medium: [6000, 13000], large: [13000, 28000] },
      tile: { small: [1800, 4500], medium: [4500, 11000], large: [10000, 22000] },
      vinyl: { small: [1200, 3000], medium: [3000, 7500], large: [6500, 14000] },
      laminate: { small: [1000, 2500], medium: [2500, 6000], large: [5500, 12000] },
      carpet: { small: [800, 2000], medium: [2000, 5000], large: [4500, 10000] },
    };
    return map[scope]?.[size] || null;
  },
};

const ROOFING = {
  slug: "roofing",
  path: "/roofing-cost-calculator",
  statePathBase: "/roofing-cost",
  name: "Roof Replacement",
  shortName: "Roofing",
  description: "Estimate roof replacement, repair, or new install costs by material.",
  image: "https://images.pexels.com/photos/259751/pexels-photo-259751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  icon: "home",
  timeline: "Most roof replacements take 1–3 days; metal or tile may take longer.",
  tips: [
    "Schedule roofing in late fall or winter — contractors often discount 10–20%.",
    "Architectural shingles last 25–30 years vs. 15–20 for 3-tab — better long-term value.",
    "Always get a written warranty on both materials and labor.",
  ],
  seo: {
    title: "Roof Replacement Cost Calculator | HavenCostGuide",
    description: "Free roof replacement cost calculator with state-specific pricing for asphalt shingles, architectural, metal, and tile roofing.",
  },
  faqs: [
    {
      q: "How much does it cost to replace a roof?",
      a: "A typical asphalt shingle roof replacement costs $8,000–$18,000 for a medium-sized home. Architectural shingles run 20–30% higher. Metal, slate, or clay tile roofs can reach $21,000–$42,000 or more.",
    },
    {
      q: "How long does a new roof last?",
      a: "3-tab asphalt shingles last 15–20 years. Architectural shingles last 25–30 years. Metal roofs last 40–70 years. Slate and clay tile can last 75–100+ years with proper installation.",
    },
    {
      q: "Do I need a permit for a roof replacement?",
      a: "Yes — most U.S. municipalities require a permit for full roof replacements. Permits typically cost $150–$500 and ensure the work meets local building codes for wind, snow, and fire ratings.",
    },
    {
      q: "When is the cheapest time of year to replace a roof?",
      a: "Late fall and winter are usually the cheapest. Roofers are less busy and often discount labor 10–20%. Spring and early summer are peak season with the highest prices.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Project Type",
      question: "What's the roofing job?",
      options: [
        { value: "replacement", label: "Full replacement", hint: "Tear-off and new roof" },
        { value: "repair", label: "Repair only", hint: "Patch leaks or partial sections" },
        { value: "new", label: "New roof install", hint: "First-time install on new structure" },
        { value: "full_layout", label: "Replacement + structural", hint: "Includes decking or framing changes" },
      ],
    },
    {
      key: "size",
      title: "Roof Size",
      question: "How large is the roof?",
      options: [
        { value: "small", label: "Small", hint: "Under 1,500 sq ft" },
        { value: "medium", label: "Medium", hint: "1,500–2,500 sq ft" },
        { value: "large", label: "Large", hint: "Over 2,500 sq ft" },
      ],
    },
    {
      key: "quality",
      title: "Material Quality",
      question: "Which material tier?",
      options: [
        { value: "budget", label: "Budget", hint: "3-tab asphalt shingles" },
        { value: "mid", label: "Mid-range", hint: "Architectural shingles" },
        { value: "high", label: "High-end", hint: "Metal, slate, or clay tile" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      replacement: { small: [5000, 10000], medium: [8000, 18000], large: [15000, 30000] },
      full_layout: { small: [7500, 14000], medium: [12000, 24000], large: [21000, 42000] },
      repair: { small: [400, 1500], medium: [800, 3000], large: [1500, 5500] },
      new: { small: [6000, 12000], medium: [10000, 22000], large: [18000, 36000] },
    };
    return map[scope]?.[size] || null;
  },
};

const DECK = {
  slug: "deck",
  path: "/deck-cost-calculator",
  statePathBase: "/deck-cost",
  name: "Deck Construction",
  shortName: "Deck",
  description: "Estimate deck construction costs by material — pressure-treated, cedar, composite, or hardwood.",
  image: "/calc-heroes/deck.jpg",
  icon: "square-stack",
  timeline: "Most decks are built in 1–3 weeks once permits are pulled.",
  tips: [
    "Composite decking costs 2–3× pressure-treated upfront but lasts 25+ years with no staining.",
    "Skip the built-in benches and planters — they add labor cost and rarely add resale value.",
    "Build in late fall or winter for a 10–20% labor discount in most U.S. markets.",
  ],
  seo: {
    title: "Deck Cost Calculator 2026 | HavenCostGuide",
    description: "Free deck construction cost calculator with state-specific pricing for pressure-treated, cedar, composite, and hardwood decks.",
  },
  faqs: [
    {
      q: "How much does it cost to build a deck in 2026?",
      a: "A typical 200–400 sq ft deck costs $5,500–$15,000 in pressure-treated lumber and $14,000–$30,000 in composite. Premium materials like ipe hardwood or full-stainless cable rails push to $25,000–$50,000.",
    },
    {
      q: "Is a composite deck worth the extra cost?",
      a: "For homeowners who plan to stay 7+ years, yes. Composite has roughly 2× the upfront cost but eliminates 25+ years of staining, sealing, and board replacement. Total 25-year cost is similar.",
    },
    {
      q: "Do I need a permit for a new deck?",
      a: "Most U.S. cities require a permit for any deck more than 30 inches off the ground or attached to the house. Permits run $150–$600. Free-standing low-platform decks are sometimes exempt.",
    },
    {
      q: "What's the cheapest deck material?",
      a: "Pressure-treated pine is by far the cheapest at $15–$25/sq ft installed. Cedar runs $25–$45/sq ft. Composite (Trex, TimberTech) is $35–$60/sq ft. Hardwoods like ipe or cumaru are $50–$90/sq ft.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Material",
      question: "What deck material?",
      options: [
        { value: "pressure_treated", label: "Pressure-treated lumber", hint: "Most affordable, requires staining" },
        { value: "cedar", label: "Cedar / redwood", hint: "Mid-tier natural wood" },
        { value: "composite", label: "Composite (Trex, TimberTech)", hint: "Low-maintenance synthetic" },
        { value: "hardwood", label: "Tropical hardwood", hint: "Ipe, cumaru, mahogany" },
      ],
    },
    {
      key: "size",
      title: "Deck Size",
      question: "How big is the deck?",
      options: [
        { value: "small", label: "Small", hint: "Under 200 sq ft" },
        { value: "medium", label: "Medium", hint: "200–400 sq ft" },
        { value: "large", label: "Large", hint: "Over 400 sq ft" },
      ],
    },
    {
      key: "quality",
      title: "Quality Level",
      question: "What quality level are you aiming for?",
      options: [
        { value: "budget", label: "Budget", hint: "Standard rails, no extras" },
        { value: "mid", label: "Mid-range", hint: "Upgraded railings + lighting" },
        { value: "high", label: "High-end", hint: "Premium rails, built-ins, lighting" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      pressure_treated: { small: [2500, 5000], medium: [5500, 11000], large: [11000, 22000] },
      cedar: { small: [4000, 8500], medium: [9000, 18000], large: [18000, 36000] },
      composite: { small: [6500, 12500], medium: [14000, 28000], large: [28000, 55000] },
      hardwood: { small: [9000, 18000], medium: [20000, 38000], large: [40000, 75000] },
    };
    return map[scope]?.[size] || null;
  },
};

const BASEMENT = {
  slug: "basement",
  path: "/basement-finishing-cost-calculator",
  statePathBase: "/basement-finishing-cost",
  name: "Basement Finishing",
  shortName: "Basement",
  description: "Estimate basement finishing costs — framing, drywall, flooring, electrical, and full conversions.",
  image: "/calc-heroes/basement.png",
  icon: "warehouse",
  timeline: "Most basement finishing projects take 4–10 weeks depending on scope.",
  tips: [
    "Address moisture before framing — a dehumidifier and any needed waterproofing should be done first.",
    "Frame ceilings with a drop-tile system if you'll need future plumbing or electrical access.",
    "Adding an egress window is required for any below-grade bedroom and adds $2,500–$5,500.",
  ],
  seo: {
    title: "Basement Finishing Cost Calculator 2026 | HavenCostGuide",
    description: "Free basement finishing cost calculator with state-specific estimates for framing, drywall, flooring, electrical, and full conversions.",
  },
  faqs: [
    {
      q: "How much does it cost to finish a basement?",
      a: "A typical 800–1,200 sq ft basement finish runs $25,000–$55,000 at mid-range quality. Budget jobs (basic framing, drywall, paint, flooring) start around $15,000. High-end finishes with bathrooms, kitchenettes, and built-ins can reach $80,000–$120,000.",
    },
    {
      q: "Does finishing my basement add value?",
      a: "Yes — Remodeling Magazine's Cost vs. Value report shows basement finishes recoup 70–86% of cost at resale, one of the highest-ROI renovations. The additional living space also improves daily livability.",
    },
    {
      q: "Do I need a permit to finish my basement?",
      a: "Yes — virtually all U.S. cities require permits for basement finishing because it involves electrical, plumbing, framing, and egress code. Permits run $300–$900 and protect resale value.",
    },
    {
      q: "How long does basement finishing take?",
      a: "Most basement finishes take 4–10 weeks. Simpler projects (framing, drywall, flooring only) can finish in 3–4 weeks. Adding a bathroom or kitchenette adds 2–3 weeks.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Project Scope",
      question: "What's included in the finish?",
      options: [
        { value: "basic", label: "Basic finish", hint: "Framing, drywall, flooring, paint" },
        { value: "with_bath", label: "Finish + bathroom", hint: "Adds a 3-piece bath" },
        { value: "full_suite", label: "Full living suite", hint: "Bath, kitchenette, bedroom" },
        { value: "premium", label: "Premium build-out", hint: "Theater room, built-ins, custom" },
      ],
    },
    {
      key: "size",
      title: "Basement Size",
      question: "How big is the basement?",
      options: [
        { value: "small", label: "Small", hint: "Under 800 sq ft" },
        { value: "medium", label: "Medium", hint: "800–1,200 sq ft" },
        { value: "large", label: "Large", hint: "Over 1,200 sq ft" },
      ],
    },
    {
      key: "quality",
      title: "Quality Level",
      question: "What quality level are you aiming for?",
      options: [
        { value: "budget", label: "Budget", hint: "Builder-grade finishes" },
        { value: "mid", label: "Mid-range", hint: "Standard upgrades" },
        { value: "high", label: "High-end", hint: "Premium materials" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      basic: { small: [10000, 22000], medium: [16000, 32000], large: [24000, 48000] },
      with_bath: { small: [16000, 32000], medium: [24000, 48000], large: [34000, 65000] },
      full_suite: { small: [22000, 42000], medium: [32000, 60000], large: [45000, 85000] },
      premium: { small: [30000, 55000], medium: [45000, 80000], large: [65000, 115000] },
    };
    return map[scope]?.[size] || null;
  },
};

const WINDOWS = {
  slug: "windows",
  path: "/window-replacement-cost-calculator",
  statePathBase: "/window-replacement-cost",
  name: "Window Replacement",
  shortName: "Windows",
  description: "Estimate window replacement costs by material, size, and quantity — vinyl, wood, fiberglass, or custom.",
  image: "https://images.unsplash.com/photo-1480074568708-e7b720bb3f09?auto=format&fit=crop&w=940&q=80",
  icon: "panels-top-left",
  timeline: "Most full-house window replacements take 2–5 days once windows are on site.",
  tips: [
    "Order windows 6–10 weeks before installation — that's the typical lead time for custom sizes.",
    "Vinyl is the best dollar-for-dollar material for most homes; fiberglass is worth it for harsh climates.",
    "Replace all windows together — one-off swaps cost 30–40% more per window in labor mobilization.",
  ],
  seo: {
    title: "Window Replacement Cost Calculator 2026 | HavenCostGuide",
    description: "Free window replacement cost calculator with state-specific pricing for vinyl, wood, fiberglass, and custom windows.",
  },
  faqs: [
    {
      q: "How much does it cost to replace windows?",
      a: "A typical full-house window replacement (10–15 windows) costs $7,500–$22,000 in vinyl and $14,000–$36,000 in fiberglass or wood. Per-window costs run $400–$1,200 installed for vinyl and $700–$2,500 for premium materials.",
    },
    {
      q: "Are vinyl or fiberglass windows better?",
      a: "Vinyl is the better value for most homeowners — lower cost, similar lifespan (25–30 years), good energy efficiency. Fiberglass costs 30–50% more but performs better in extreme climates and accepts paint, making it the choice for harsh climates and historic homes.",
    },
    {
      q: "Do new windows pay for themselves in energy savings?",
      a: "Energy Star windows save $125–$465/year on average for a typical U.S. home, per the EPA. Payback periods run 8–25 years on materials alone — the bigger ROI is from avoiding repair costs and lifting resale value.",
    },
    {
      q: "How long do new windows last?",
      a: "Vinyl: 25–30 years. Fiberglass: 30–50 years. Wood: 30+ years with maintenance. Aluminum: 20–25 years. Manufacturer warranties typically run 20 years on glass and 10–lifetime on frames.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Window Material",
      question: "What window material?",
      options: [
        { value: "vinyl", label: "Vinyl", hint: "Best value, low maintenance" },
        { value: "fiberglass", label: "Fiberglass", hint: "Premium performance, paintable" },
        { value: "wood", label: "Wood / wood-clad", hint: "Classic look, most maintenance" },
        { value: "aluminum", label: "Aluminum", hint: "Modern, slim profiles" },
      ],
    },
    {
      key: "size",
      title: "Number of Windows",
      question: "How many windows are you replacing?",
      options: [
        { value: "small", label: "Small job", hint: "1–5 windows" },
        { value: "medium", label: "Medium job", hint: "6–15 windows" },
        { value: "large", label: "Whole-house", hint: "16+ windows" },
      ],
    },
    {
      key: "quality",
      title: "Quality Level",
      question: "What quality level are you aiming for?",
      options: [
        { value: "budget", label: "Budget", hint: "Single-hung, dual-pane" },
        { value: "mid", label: "Mid-range", hint: "Energy Star, low-E coating" },
        { value: "high", label: "High-end", hint: "Triple-pane, custom shapes" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      vinyl: { small: [1800, 4500], medium: [5500, 13000], large: [12000, 26000] },
      fiberglass: { small: [3000, 7000], medium: [9000, 20000], large: [18000, 40000] },
      wood: { small: [3500, 8500], medium: [11000, 24000], large: [22000, 48000] },
      aluminum: { small: [2200, 5500], medium: [7000, 16000], large: [14000, 32000] },
    };
    return map[scope]?.[size] || null;
  },
};

const SOLAR = {
  slug: "solar",
  path: "/solar-panel-cost-calculator",
  statePathBase: "/solar-panel-cost",
  name: "Solar Panel Install",
  shortName: "Solar",
  description: "Estimate the cost of a residential solar panel system — by system size, panel quality, and whether you add battery backup.",
  image: "https://images.pexels.com/photos/9875415/pexels-photo-9875415.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  icon: "sun",
  timeline: "Most residential solar installs take 1–3 days on-site once permits and utility approval clear (typically 6–12 weeks total project timeline).",
  tips: [
    "The 30% federal Residential Clean Energy Credit expired for systems installed after Dec 31, 2025 — quotes you get now are the real gross cost. State/utility incentives may still apply.",
    "Microinverters cost ~10–15% more than string inverters but pay back via better shade tolerance and per-panel monitoring.",
    "Add a battery later, not now, unless your utility has poor net-metering — battery payback periods are 12–20 years for most U.S. homeowners.",
  ],
  seo: {
    title: "Solar Panel Cost Calculator 2026 | HavenCostGuide",
    description: "Free solar panel cost calculator with state-specific pricing for residential PV systems, microinverters, and battery storage.",
  },
  faqs: [
    {
      q: "How much does a solar panel system cost in 2026?",
      a: "A typical residential solar system costs $22,000–$35,000 installed for a 10 kW system at mid-range quality. (The 30% federal Residential Clean Energy Credit expired for systems placed in service after Dec 31, 2025, so plan on gross cost minus any state/utility incentives.) Smaller 6 kW systems run $13,000–$22,000; large 15 kW systems can reach $32,000–$52,000.",
    },
    {
      q: "Is solar still worth it now that the federal tax credit has ended?",
      a: "For most U.S. homeowners with $100+ monthly electric bills and at least 5+ years remaining at the property, yes — even without the federal credit, which expired Dec 31, 2025. Typical payback periods in 2026 run 10–14 years on gross cost; system lifespan is 25–30+ years. Homeowners in CA, MA, NY, and NJ with strong state incentives and high electricity rates still see payback in under 8 years.",
    },
    {
      q: "Do I need a permit to install solar panels?",
      a: "Yes — every U.S. jurisdiction requires a permit for grid-tied solar installation. Permits typically run $200–$600. Most installers handle the permit and utility interconnection paperwork as part of the contract.",
    },
    {
      q: "Should I add a battery (Tesla Powerwall, Enphase) to my solar?",
      a: "Adding battery storage costs $10,000–$18,000 on top of the panel system. It's worth it if your utility has poor net-metering (CA, HI, AZ), if you have frequent power outages (FL, TX, NC), or if your state offers a battery incentive (CT, MA, NY). Otherwise, payback periods exceed 12 years.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "System Type",
      question: "What type of solar setup?",
      options: [
        { value: "panels_only", label: "Solar panels only", hint: "Grid-tied PV system, no battery" },
        { value: "panels_battery", label: "Solar + battery storage", hint: "PV system + Tesla Powerwall or equivalent" },
        { value: "battery_only", label: "Add battery to existing solar", hint: "Retrofit battery to current PV system" },
        { value: "off_grid", label: "Off-grid system", hint: "Panels + large battery bank, no utility connection" },
      ],
    },
    {
      key: "size",
      title: "System Size",
      question: "How big a system?",
      options: [
        { value: "small", label: "Small (4–6 kW)", hint: "~$100/mo electric bill" },
        { value: "medium", label: "Medium (8–10 kW)", hint: "~$150–$250/mo electric bill" },
        { value: "large", label: "Large (12–15 kW)", hint: "~$300+/mo electric bill" },
      ],
    },
    {
      key: "quality",
      title: "Quality Tier",
      question: "What quality tier?",
      options: [
        { value: "budget", label: "Budget", hint: "Tier-2 panels, string inverter" },
        { value: "mid", label: "Mid-range", hint: "Tier-1 panels, microinverters" },
        { value: "high", label: "High-end", hint: "Premium panels (REC, SunPower), high-efficiency" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      panels_only: { small: [10000, 17000], medium: [17000, 27000], large: [25000, 40000] },
      panels_battery: { small: [20000, 32000], medium: [29000, 45000], large: [40000, 62000] },
      battery_only: { small: [10000, 17000], medium: [12000, 20000], large: [16000, 28000] },
      off_grid: { small: [22000, 38000], medium: [35000, 60000], large: [50000, 90000] },
    };
    return map[scope]?.[size] || null;
  },
};

const FENCE = {
  slug: "fence",
  path: "/fence-installation-cost-calculator",
  statePathBase: "/fence-installation-cost",
  name: "Fence Installation",
  shortName: "Fence",
  description: "Estimate fence installation costs by material — wood, vinyl, chain-link, aluminum, or composite — and yard size.",
  image: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1600&q=80",
  icon: "fence",
  timeline: "Most fence installations take 2–5 days once materials arrive and permits clear.",
  tips: [
    "Survey your property line before installing — fence disputes are one of the most common neighbor lawsuits in the U.S.",
    "Vinyl fences cost 30–50% more upfront than pressure-treated wood but eliminate 20+ years of staining and repair.",
    "Skip decorative caps and post-toppers unless you're matching them across the entire run — partial use looks unfinished.",
  ],
  seo: {
    title: "Fence Installation Cost Calculator 2026 | HavenCostGuide",
    description: "Free fence installation cost calculator with state-specific pricing for wood, vinyl, chain-link, aluminum, and composite fencing.",
  },
  faqs: [
    {
      q: "How much does it cost to install a fence in 2026?",
      a: "A typical 150-linear-foot residential fence costs $3,000–$9,000 in wood and $3,750–$11,000 in vinyl. Chain-link is the cheapest at $1,800–$3,750; aluminum/wrought iron runs $4,500–$10,500. Per linear foot installed: wood $20–$45, vinyl $25–$60, chain-link $12–$25, aluminum $30–$70.",
    },
    {
      q: "Do I need a permit to install a fence?",
      a: "Most U.S. cities require a permit for fences over 6 feet tall or in front-yard areas. Permits run $50–$300. HOA approvals are separate and often add 2–6 weeks to the timeline. Property-line surveys are highly recommended even when not required.",
    },
    {
      q: "Wood vs vinyl fence — which is better?",
      a: "Vinyl wins for long-term cost (no staining or replacement boards for 20+ years) and looks. Wood wins for upfront cost ($20–$45/LF vs $25–$60/LF) and the ability to repaint/restain. Cedar lasts 15–25 years; pressure-treated pine 10–15 years; quality vinyl 25–40 years.",
    },
    {
      q: "How long does fence installation take?",
      a: "Most residential fence installs run 2–5 days. Concrete-set posts add a day for curing. Long custom runs (300+ LF) or sloped terrain can push to 7–10 days. Material lead times for vinyl and aluminum can add 2–4 weeks.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Fence Material",
      question: "What fence material?",
      options: [
        { value: "wood", label: "Wood (cedar / pressure-treated)", hint: "Most common residential choice" },
        { value: "vinyl", label: "Vinyl / PVC", hint: "Low-maintenance, 25+ year lifespan" },
        { value: "chain_link", label: "Chain link", hint: "Cheapest, utilitarian" },
        { value: "aluminum", label: "Aluminum / wrought iron", hint: "Decorative, low-maintenance" },
        { value: "composite", label: "Composite (Trex, etc.)", hint: "Premium, no maintenance" },
      ],
    },
    {
      key: "size",
      title: "Fence Length",
      question: "How much fence are you installing?",
      options: [
        { value: "small", label: "Small (50–100 LF)", hint: "Side yard or backyard partial" },
        { value: "medium", label: "Medium (100–200 LF)", hint: "Typical backyard perimeter" },
        { value: "large", label: "Large (200–400+ LF)", hint: "Full property perimeter" },
      ],
    },
    {
      key: "quality",
      title: "Quality Tier",
      question: "What quality tier?",
      options: [
        { value: "budget", label: "Budget", hint: "Standard panels, no decorative top" },
        { value: "mid", label: "Mid-range", hint: "Better-grade material, caps included" },
        { value: "high", label: "High-end", hint: "Premium material, decorative posts, lattice top" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      wood:       { small: [1500, 3500], medium: [3000, 6500], large: [6000, 13000] },
      vinyl:      { small: [2000, 4500], medium: [3800, 8500], large: [7500, 17000] },
      chain_link: { small: [900, 1900],  medium: [1800, 3700], large: [3500, 7500]  },
      aluminum:   { small: [2400, 5400], medium: [4500, 10500], large: [9000, 21000] },
      composite:  { small: [2800, 6500], medium: [5500, 13000], large: [11000, 26000] },
    };
    return map[scope]?.[size] || null;
  },
};

const POOL = {
  slug: "pool",
  path: "/pool-installation-cost-calculator",
  statePathBase: "/pool-installation-cost",
  name: "In-Ground Pool Install",
  shortName: "Pool",
  description: "Estimate in-ground pool installation costs — vinyl liner, fiberglass, or gunite/concrete — plus deck and equipment.",
  image: "https://images.pexels.com/photos/261172/pexels-photo-261172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  icon: "waves",
  timeline: "Vinyl liner pools take 3–6 weeks; fiberglass 2–4 weeks; concrete/gunite 8–14 weeks from excavation to first swim.",
  tips: [
    "Add 25–40% to the pool price for the surrounding deck, fencing, and landscaping — most homeowners forget these in the budget.",
    "Saltwater systems cost $1,500–$2,500 more upfront than chlorine but cut annual chemical costs by 40–60%.",
    "A pool typically adds 5–8% to home value in warm-climate states (FL, AZ, TX, CA) but only 0–3% in cold-climate states.",
  ],
  seo: {
    title: "In-Ground Pool Cost Calculator 2026 | HavenCostGuide",
    description: "Free in-ground pool installation cost calculator with state-specific pricing for vinyl liner, fiberglass, and concrete pools.",
  },
  faqs: [
    {
      q: "How much does an in-ground pool cost in 2026?",
      a: "Vinyl liner pools cost $30,000–$60,000 installed. Fiberglass pools run $40,000–$85,000. Concrete/gunite pools start at $55,000 and can exceed $110,000 with custom shapes and features. Add 25–40% for the surrounding deck, fence, and landscaping — most homeowners forget these in their budget.",
    },
    {
      q: "Which pool type is best — vinyl, fiberglass, or concrete?",
      a: "Vinyl liner: cheapest upfront ($30k–$60k) but liners need replacement every 8–12 years ($4k–$8k each). Fiberglass: fast install (2–4 weeks), low maintenance, limited shape options ($40k–$85k). Concrete: fully customizable, 50+ year lifespan, but slowest install and highest maintenance ($55k–$110k+).",
    },
    {
      q: "Do I need a permit for an in-ground pool?",
      a: "Yes — every U.S. jurisdiction requires a permit for in-ground pool construction. Permits run $400–$1,500. Most municipalities also require code-compliant fencing (minimum 48–60 inches, self-closing gates) before the pool can be filled.",
    },
    {
      q: "How much does pool maintenance cost per year?",
      a: "Annual pool maintenance costs $1,200–$3,500 in 2026 for chlorine pools (chemicals, equipment, electricity, and water). Saltwater systems cost less in chemicals ($400–$800/yr) but require cell replacement every 3–7 years ($800–$1,500). Add another $500–$1,500 for opening/closing service in cold-climate states.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Pool Type",
      question: "What type of pool?",
      options: [
        { value: "vinyl_liner", label: "Vinyl liner", hint: "Most affordable in-ground option" },
        { value: "fiberglass", label: "Fiberglass", hint: "Fast install, smooth surface" },
        { value: "concrete", label: "Concrete / gunite", hint: "Fully custom shapes and features" },
        { value: "above_ground", label: "Above-ground", hint: "Cheapest option, no excavation" },
      ],
    },
    {
      key: "size",
      title: "Pool Size",
      question: "How big a pool?",
      options: [
        { value: "small", label: "Small (under 300 sq ft)", hint: "Cocktail/plunge pool" },
        { value: "medium", label: "Medium (300–500 sq ft)", hint: "Standard family pool" },
        { value: "large", label: "Large (500+ sq ft)", hint: "Resort-style pool" },
      ],
    },
    {
      key: "quality",
      title: "Quality Tier",
      question: "What quality tier?",
      options: [
        { value: "budget", label: "Budget", hint: "Basic pool, chlorine, minimal features" },
        { value: "mid", label: "Mid-range", hint: "Saltwater, LED lighting, basic decking" },
        { value: "high", label: "High-end", hint: "Custom shape, waterfall, premium decking" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      vinyl_liner:  { small: [22000, 38000], medium: [30000, 55000], large: [42000, 75000] },
      fiberglass:   { small: [32000, 55000], medium: [42000, 72000], large: [58000, 95000] },
      concrete:     { small: [42000, 72000], medium: [58000, 95000], large: [80000, 145000] },
      above_ground: { small: [2500, 5500],   medium: [4000, 9500],   large: [7000, 16000]  },
    };
    return map[scope]?.[size] || null;
  },
};

const PAINTING = {
  slug: "painting",
  path: "/painting-cost-calculator",
  statePathBase: "/painting-cost",
  name: "Interior & Exterior Painting",
  shortName: "Painting",
  description: "Estimate interior or exterior painting costs by scope, home size, and finish quality — labor, paint, and prep work included.",
  image: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=1600&q=80",
  icon: "paintbrush",
  timeline: "Interior painting takes 1–3 days per room; whole-home interior 5–10 days; full exterior 4–7 days with proper prep and drying time.",
  tips: [
    "Two coats of premium paint usually beats three coats of cheap paint — premium paint covers better, fades less, and costs ~$25/gallon more for a job that lasts 4-6 more years.",
    "Always pay for prep work (caulking, patching, sanding) as a separate line — skipping prep is the #1 reason paint jobs fail within 18 months.",
    "Get the paint sheen specified in writing: flat / matte for ceilings, eggshell or satin for walls, semi-gloss for trim and bathrooms. Wrong sheen is a common change-order trigger.",
  ],
  seo: {
    title: "Painting Cost Calculator 2026 — Interior & Exterior",
    description: "Free interior and exterior painting cost calculator with state-specific pricing for whole-home and per-room jobs in 2026.",
  },
  faqs: [
    {
      q: "How much does it cost to paint a house in 2026?",
      a: "Interior painting averages $2–$6 per sqft of floor space (whole-home: $3,500–$10,000 for a 1,500–2,500 sqft home). Exterior painting averages $1.50–$4.50 per sqft of wall surface ($4,000–$12,000 for a typical two-story home). Quality of paint, prep work needed, and trim complexity drive most of the variance.",
    },
    {
      q: "Interior vs exterior painting cost — which is more?",
      a: "Per square foot, exterior is typically cheaper ($1.50–$4.50/sqft of wall) than interior ($2–$6/sqft of floor). But exteriors usually have more total surface area and require expensive prep (power wash, scraping, primer), so totals tend to be similar for a comparable house — $5,000–$10,000 either way.",
    },
    {
      q: "How long does paint last on a house?",
      a: "Interior paint: 5–10 years for bedrooms, 3–5 years for high-traffic areas (kitchen, bathroom, kids' rooms). Exterior paint: 5–7 years on wood siding, 10–15 years on stucco or fiber cement, 4–6 years on harsh-weather exposures (south-facing walls in the Sun Belt or storm-exposed coastal homes).",
    },
    {
      q: "Do painters paint over wallpaper or strip it first?",
      a: "Stripping is recommended (and standard) for any wallpaper job. Painting over wallpaper risks bubbling, peeling, and seam-line bleed-through within 12-24 months. Strip + skim-coat + prime + paint costs $5–$9/sqft extra but is the only durable approach. Some contractors will paint over wallpaper if you sign a no-warranty waiver — don't.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Project Scope",
      question: "What are you painting?",
      options: [
        { value: "single_room", label: "Single room", hint: "One bedroom, office, or living room" },
        { value: "whole_interior", label: "Whole interior", hint: "Multiple rooms / full home interior" },
        { value: "whole_exterior", label: "Whole exterior", hint: "Full home exterior + trim" },
        { value: "interior_plus_exterior", label: "Interior + Exterior", hint: "Complete home repaint, inside + out" },
      ],
    },
    {
      key: "size",
      title: "Home / Room Size",
      question: "What's the size?",
      options: [
        { value: "small", label: "Small (room <200 sqft / home <1,500 sqft)" },
        { value: "medium", label: "Medium (room 200-350 sqft / home 1,500-2,500 sqft)" },
        { value: "large", label: "Large (room 350+ sqft / home 2,500+ sqft)" },
      ],
    },
    {
      key: "quality",
      title: "Quality Tier",
      question: "What quality tier?",
      options: [
        { value: "budget", label: "Budget", hint: "Builder-grade paint, minimal prep, 1 coat" },
        { value: "mid", label: "Mid-range", hint: "Premium paint, standard prep, 2 coats" },
        { value: "high", label: "High-end", hint: "Designer paint, full prep + caulk + patch, 2-3 coats" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      single_room:           { small: [300, 700],   medium: [500, 1100],  large: [800, 1700]   },
      whole_interior:        { small: [2200, 4500], medium: [3800, 7500], large: [5800, 11500] },
      whole_exterior:        { small: [2800, 5500], medium: [4500, 8500], large: [6800, 13500] },
      interior_plus_exterior:{ small: [4800, 9200], medium: [7800, 14500], large: [11500, 23000] },
    };
    return map[scope]?.[size] || null;
  },
};

// HARDSCAPE — patio, walkway, retaining wall, fire-pit installs. Surfaced
// from the 2026-06 hallucination discovery cycle: /hardscape-cost-{state}
// was getting 55 hits/cycle landing on a 404 (now redirected to the editorial
// guide). Promoting it to a first-class calc converts that traffic into the
// state-landing lead-capture flow used by the 11 existing project calcs.
//
// Cost data sourced from /app/frontend/src/guides/hardscape-cost-*-2026.jsx
// 50-state editorial guides — base ranges normalized to the median state and
// adjusted at runtime via the standard state multiplier system.
const HARDSCAPE = {
  slug: "hardscape",
  path: "/hardscape-cost-calculator",
  statePathBase: "/hardscape-cost",
  name: "Hardscape Installation",
  shortName: "Hardscape",
  description: "Estimate hardscape installation costs by element — paver patios, stone walkways, retaining walls, or fire pits. State-adjusted pricing for 2026.",
  image: "/calc-heroes/hardscape.png",
  icon: "shapes",
  timeline: "Paver patios install in 3–7 days; retaining walls run 5–14 days depending on engineering and drainage; fire pits are typically a 1–2 day job.",
  tips: [
    "Always specify base depth in writing — proper hardscape installation needs 4–6 inches of compacted gravel base. Skipping base prep is the #1 cause of paver heave within 3 winters.",
    "Engineered retaining walls (over 4 feet tall) need a structural engineer's stamp in most municipalities — budget $500–$1,500 for the engineering and $200–$600 for the permit.",
    "Pick locally-quarried stone when possible — interstate freight on natural stone can run $200–$400/ton and quickly doubles the materials line on a stone-heavy project.",
  ],
  seo: {
    title: "Hardscape Cost Calculator 2026 — Patio, Walkway, Wall, Fire Pit",
    description: "Free hardscape cost calculator with state-specific 2026 pricing for paver patios, stone walkways, retaining walls, and fire pits.",
  },
  faqs: [
    {
      q: "How much does hardscape installation cost in 2026?",
      a: "A typical 300 sqft paver patio runs $4,500–$12,000 installed depending on material (concrete pavers cheapest, natural stone most expensive). Retaining walls average $25–$75/sqft of wall face. Fire pits range from $700 (basic block kit) to $6,500 (custom stone with seating wall). Walkways run $15–$45/sqft installed.",
    },
    {
      q: "Concrete pavers vs natural stone — which is cheaper?",
      a: "Concrete pavers are 30–50% cheaper than natural stone at install (typical $10–$22/sqft vs $20–$40/sqft for bluestone or flagstone). Concrete pavers also last 25–40 years with negligible maintenance. Natural stone has a stronger resale signal but the price gap rarely pays back for homeowners not in the top-third home-value tier of their neighborhood.",
    },
    {
      q: "Do I need a permit for hardscape work?",
      a: "Most U.S. municipalities require a permit for retaining walls over 4 feet tall and for any work that alters drainage or grading near property lines. Patios and walkways are typically permit-exempt unless they're attached to the foundation or change impervious surface coverage above local thresholds. Always check before you dig — failed inspections cost more than the permit ever would.",
    },
    {
      q: "Will hardscape add to my home's resale value?",
      a: "Well-designed hardscape in a homeowner's primary outdoor entertaining zone (back patio, fire pit area, front entry walkway) typically recovers 50–75% of cost at resale according to 2026 Remodeling Magazine data. Outsized projects (over-engineered retaining walls, very expensive stone choices in mid-market neighborhoods) often recover under 40%.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Element",
      question: "What hardscape element?",
      options: [
        { value: "paver_patio", label: "Paver patio", hint: "Concrete pavers or brick" },
        { value: "stone_patio_walkway", label: "Natural stone patio / walkway", hint: "Bluestone, flagstone, travertine" },
        { value: "retaining_wall", label: "Retaining wall", hint: "Block or stone, 2-6 feet tall" },
        { value: "fire_pit_seating", label: "Fire pit + seating area", hint: "Built-in stone with optional gas line" },
      ],
    },
    {
      key: "size",
      title: "Size",
      question: "How big is the project?",
      options: [
        { value: "small", label: "Small", hint: "Under 200 sqft / wall < 20 linear ft" },
        { value: "medium", label: "Medium", hint: "200-400 sqft / wall 20-40 linear ft" },
        { value: "large", label: "Large", hint: "Over 400 sqft / wall > 40 linear ft" },
      ],
    },
    {
      key: "quality",
      title: "Quality Tier",
      question: "What quality tier?",
      options: [
        { value: "budget", label: "Budget", hint: "Big-box pavers, standard install" },
        { value: "mid", label: "Mid-range", hint: "Upgraded pavers + sealed surface" },
        { value: "high", label: "High-end", hint: "Premium stone + drainage + lighting" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      paver_patio:         { small: [2800, 5500],  medium: [5500, 11000], large: [10500, 21000] },
      stone_patio_walkway: { small: [4500, 9000],  medium: [9000, 18000], large: [17000, 34000] },
      retaining_wall:      { small: [3200, 6500],  medium: [6500, 13500], large: [13000, 26500] },
      fire_pit_seating:    { small: [700, 1600],   medium: [1800, 4200],  large: [4200, 8500]   },
    };
    return map[scope]?.[size] || null;
  },
};

// LANDSCAPING — softscape installs (sod, planting beds, irrigation, mulch).
// Same hallucination-cluster origin as HARDSCAPE — 57 hits/cycle landing on
// 404 → editorial guide → now promoted to first-class calc.
const LANDSCAPING = {
  slug: "landscaping",
  path: "/landscaping-cost-calculator",
  statePathBase: "/landscaping-cost",
  name: "Landscaping Installation",
  shortName: "Landscaping",
  description: "Estimate landscaping installation costs by scope — sod, planting beds, irrigation, full backyard makeover. State-adjusted 2026 pricing.",
  image: "/calc-heroes/landscaping.png",
  icon: "trees",
  timeline: "Sod installation completes in 1–2 days; planting bed install runs 2–5 days; full backyard makeover takes 2–4 weeks including irrigation and plant establishment.",
  tips: [
    "Install irrigation BEFORE sod or planting — retrofitting a sprinkler system into established turf costs 2-3× more than doing it in the same trench as the initial install.",
    "Get a written 90-day plant warranty in the contract. Reputable landscapers warranty their plant material; storm-chasers and Craigslist crews don't.",
    "Skip the 'instant maturity' impulse — 5-gallon shrubs cost 3-4× a 1-gallon shrub but reach the same size within 24 months. Spend the savings on irrigation and soil prep instead.",
  ],
  seo: {
    title: "Landscaping Cost Calculator 2026 — Sod, Planting, Irrigation",
    description: "Free landscaping cost calculator with state-specific 2026 pricing for sod installation, planting beds, irrigation, and full backyard makeovers.",
  },
  faqs: [
    {
      q: "How much does landscaping cost in 2026?",
      a: "Sod installation runs $1.50–$3.50/sqft installed (typical 3,000 sqft backyard: $4,500–$10,500). New planting beds cost $20–$60/sqft of bed depending on plant maturity. Full irrigation systems run $3,500–$9,500 for a typical residential lot. A complete backyard makeover (sod + beds + irrigation + mulch) for a 5,000 sqft yard ranges $12,000–$35,000.",
    },
    {
      q: "Sod vs seed — which is more cost-effective?",
      a: "Seed is 70-85% cheaper upfront ($0.10–$0.30/sqft vs $1.50–$3.50/sqft for sod). But seed needs 4–10 weeks of intensive watering and weeding to establish, has a 60–80% success rate, and is unusable for the first 2–3 months. Sod is usable in 2 weeks and has a 95%+ success rate. For homes selling within 12 months or in high-traffic family yards, sod is the right choice.",
    },
    {
      q: "Do I need a permit for landscaping work?",
      a: "Most municipalities don't require permits for sod, planting, or irrigation work. You may need permits for: grade changes over 12 inches, tree removal in protected zones, irrigation system tie-ins to municipal water (some cities), and any work in a designated wetland buffer. HOAs often have their own approval process — check the bylaws.",
    },
    {
      q: "Will landscaping add to my home's resale value?",
      a: "Healthy, mature landscaping recovers 100–150% of cost at resale in most regions according to 2026 NAR Profile of Home Buying data — making it one of the few outdoor projects with above-100% ROI. The catch: the work needs 12–24 months to look established. Landscaping done within 60 days of listing typically recovers only 30–50% because buyers see fresh mulch, not established gardens.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Scope",
      question: "What's the scope?",
      options: [
        { value: "sod_only", label: "Sod installation only", hint: "Turf only, no planting" },
        { value: "planting_beds", label: "New planting beds", hint: "Shrubs, perennials, mulch" },
        { value: "irrigation_system", label: "Irrigation system", hint: "Sprinkler system + controller" },
        { value: "full_backyard", label: "Full backyard makeover", hint: "Sod + beds + irrigation + mulch" },
      ],
    },
    {
      key: "size",
      title: "Yard Size",
      question: "How big is the area?",
      options: [
        { value: "small", label: "Small", hint: "Under 2,000 sqft" },
        { value: "medium", label: "Medium", hint: "2,000-5,000 sqft" },
        { value: "large", label: "Large", hint: "Over 5,000 sqft" },
      ],
    },
    {
      key: "quality",
      title: "Quality Tier",
      question: "What quality tier?",
      options: [
        { value: "budget", label: "Budget", hint: "Standard sod, 1-gal plants, basic mulch" },
        { value: "mid", label: "Mid-range", hint: "Premium sod, 3-gal plants, hardwood mulch" },
        { value: "high", label: "High-end", hint: "Designer plant palette, 5-gal+, custom soil amendments" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      sod_only:        { small: [1800, 3500],  medium: [4200, 8500],  large: [8500, 17000]  },
      planting_beds:   { small: [2400, 5000],  medium: [5500, 11500], large: [11000, 22000] },
      irrigation_system: { small: [2800, 5500], medium: [4200, 8500], large: [6500, 12500]  },
      full_backyard:   { small: [7500, 15000], medium: [14000, 28000], large: [25000, 50000] },
    };
    return map[scope]?.[size] || null;
  },
};



const DOOR = {
  slug: "door",
  path: "/door-replacement-cost-calculator",
  statePathBase: "/door-replacement-cost",
  name: "Door Replacement",
  shortName: "Doors",
  description: "Estimate door replacement costs — interior doors, front entry, sliding patio, or french doors — with state-adjusted 2026 pricing.",
  image: "/calc-heroes/door-replacement.jpg",
  icon: "door-open",
  timeline: "Interior door swaps run 1–3 hours each; exterior entry doors typically 4–6 hours; patio sliding doors 1 full day. Custom or built-in-frame doors can add 1–2 weeks for fabrication.",
  tips: [
    "A new fiberglass entry door is one of the highest-ROI exterior upgrades in 2026 — NAR's Remodeling Impact Report puts cost recovery at 100%+ for mid-market homes.",
    "If you're replacing more than 5 interior doors, ask for a 'volume swap' contractor — labor per door drops 20–35% when they're set up for a single-day blitz.",
    "Pre-hung doors (frame included) install in roughly half the time of slab-only replacements. Pay the $80–$140 premium per door if your existing jambs are damaged or out-of-square.",
  ],
  seo: {
    title: "Door Replacement Cost Calculator 2026 | HavenCostGuide",
    description: "Free 2026 door replacement cost calculator — interior, entry, sliding patio, and french doors. State-adjusted pricing with labor + materials + permits breakdown.",
  },
  faqs: [
    {
      q: "How much does it cost to replace a door in 2026?",
      a: "Interior pre-hung doors run $250–$700 installed; mid-range solid-core interior doors are $450–$1,100. Steel entry doors install for $700–$1,900; fiberglass entry doors run $1,200–$3,800. Sliding-glass patio doors typically cost $1,800–$4,800 installed; french exterior doors $3,500–$8,500. A whole-house door swap (6–8 interior doors + 1 entry + 1 patio) usually lands between $7,500 and $22,000 depending on materials.",
    },
    {
      q: "Should I replace just the slab or the entire pre-hung door?",
      a: "Slab-only replacement saves $80–$200 per door BUT only works if the existing jamb is straight, undamaged, and the new slab matches the old slab's hinge positions and dimensions exactly. If your existing jambs are 25+ years old, painted multiple times, or any door currently sticks/binds, replace the full pre-hung unit. The labor savings from skipping jamb work disappear if the new slab needs custom mortising.",
    },
    {
      q: "Is a fiberglass entry door worth the premium over steel?",
      a: "For most U.S. homeowners, yes. Fiberglass entry doors cost roughly 60% more than steel ($1,200–$3,800 vs $700–$1,900 installed) but they don't dent, don't rust, hold paint indefinitely, and offer 4–5× the R-value of bare steel. Steel is the better value only in mild climates with low traffic and where you don't mind repainting every 8–10 years.",
    },
    {
      q: "Do I need a permit to replace a door?",
      a: "Interior door swaps almost never require a permit. Exterior door replacements vary: same-size, same-position swaps usually don't require a permit, but enlarging an opening, cutting a new opening, or changing a structural header always does. Permit costs run $40–$280. HOAs in townhome/condo communities often require approval even when the city doesn't.",
    },
    {
      q: "How long do new doors last?",
      a: "Solid-core interior doors: 30+ years. Hollow-core interior doors: 12–20 years before hollow damage shows. Steel entry doors: 15–25 years (paint life is the limiter). Fiberglass entry doors: 30+ years. Wood entry doors: 15–30 years depending on exposure and refinishing cycle. Sliding patio doors: 20–30 years before rollers + weather seals need replacement.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Door Type",
      question: "What type of door replacement?",
      options: [
        { value: "interior", label: "Interior doors", hint: "Bedroom, closet, bathroom — pre-hung or slab" },
        { value: "entry", label: "Front / back entry door", hint: "Steel, fiberglass, or solid wood exterior" },
        { value: "patio", label: "Sliding patio door", hint: "2- or 3-panel glass slider" },
        { value: "french", label: "French / double doors", hint: "Hinged double-door exterior or interior" },
        { value: "whole_house", label: "Whole-house swap", hint: "All interior + entry + patio" },
      ],
    },
    {
      key: "size",
      title: "How Many Doors",
      question: "How many doors are you replacing?",
      options: [
        { value: "small", label: "1 door", hint: "Single swap" },
        { value: "medium", label: "2–4 doors", hint: "Partial swap" },
        { value: "large", label: "5+ doors", hint: "Whole-house or large project" },
      ],
    },
    {
      key: "quality",
      title: "Quality Level",
      question: "What quality level?",
      options: [
        { value: "budget", label: "Budget", hint: "Builder-grade, big-box stock" },
        { value: "mid", label: "Mid-range", hint: "Solid-core / fiberglass / mid-tier sliding" },
        { value: "high", label: "High-end", hint: "Custom wood / premium fiberglass / wide multi-slider" },
      ],
    },
  ],
  // 2026 installed pricing, normalized BEFORE state + quality multipliers.
  // Interior single-door, "small" baseline = ~$300-$700 budget swap; scales
  // up with door count and category.
  baseRanges: ({ scope, size }) => {
    const map = {
      interior:    { small: [250, 700],    medium: [900, 2400],   large: [2400, 6500] },
      entry:       { small: [900, 2500],   medium: [1900, 5500],  large: [4500, 11500] },
      patio:       { small: [1800, 4800],  medium: [4000, 10500], large: [8500, 20000] },
      french:      { small: [3500, 8500],  medium: [7200, 17500], large: [14000, 32000] },
      whole_house: { small: [4500, 11000], medium: [9000, 22000], large: [16000, 38000] },
    };
    return map[scope]?.[size] || null;
  },
};

const HVAC = {
  slug: "hvac",
  path: "/hvac-cost-calculator",
  statePathBase: "/hvac-cost",
  name: "HVAC System (AC + Heat Pump)",
  shortName: "HVAC",
  description: "Estimate full HVAC system cost — central AC + furnace OR all-electric heat pump — with state-adjusted 2026 pricing and HEEHRA / utility rebate overlay.",
  image: "/calc-heroes/hvac.jpg",
  pinImage: "/pins/energy-calcs/hvac.jpg",
  icon: "wind",
  // Drives the rebate overlay on ResultsView. Heat-pump map only — HEEHRA
  // and state/utility incentives remain after the 25C expiry.
  energyRebateProjectSlug: "heat-pump",
  timeline: "Replacement timelines: central AC swap 1 day; furnace swap 1–2 days; full split-system replacement 2–3 days; heat-pump install 2–4 days (longer in cold-climate states needing supplemental electric strip heat). Add 1–2 weeks lead-time for utility-rebate paperwork pre-approval.",
  tips: [
    "The federal 25C credit expired Dec 31, 2025 — compare heat pumps and gas systems on gross cost, HEEHRA eligibility, state/utility rebates, and operating cost.",
    "In cold-climate states (MN/WI/ND/ME/MT/NH/VT), spec a cold-climate-rated heat pump (CCHP) with backup electric strip heat OR keep a gas furnace as backup for dual-fuel resilience below ~5°F.",
    "Manual J load calc ($150–$400 from a 3rd-party HVAC consultant) saves more than its cost — most contractors oversize systems by 25–40%, hurting humidity control AND efficiency.",
    "Stack state credits, utility rebates, and HEEHRA POS rebate (if income-qualified). Don't assume your contractor knows the full stack — bring a printout from /state-utility-rebate-lookup.",
    "Heat pump SEER2 16+ and HSPF2 8+ is the 2026 efficiency sweet spot — beyond that, marginal payback >12 years for most climates.",
  ],
  seo: {
    title: "HVAC Cost Calculator 2026 — Heat Pump vs Central AC + Furnace",
    description: "Free 2026 HVAC cost calculator with HEEHRA/state/utility rebate overlay. Compare central AC + gas furnace ($5K-$15K) vs heat pump ($8K-$22K) — federal 25C expired Dec 31, 2025.",
  },
  faqs: [
    {
      q: "How much does a new HVAC system cost in 2026?",
      a: "Central AC + gas furnace replacement: $7,000–$15,000 installed nationally. All-electric heat pump system: $10,000–$22,000 (or $14,000–$28,000 for cold-climate ducted CCHP). Mini-split / ductless heat pump: $4,500–$15,000 per 2–4-zone system. State multipliers swing pricing ±30% — CA/HI/NY top the chart, MS/AR/OK at the floor.",
    },
    {
      q: "Should I replace with a heat pump or a traditional AC + furnace?",
      a: "For many US homeowners in 2026, a heat pump still wins on total cost once you factor HEEHRA (income-qualified), state/utility rebates, and lower lifetime operating cost. The federal 25C credit expired Dec 31, 2025, so it no longer changes the math. The exception: cold-climate states where electricity is $0.20+/kWh and gas is sub-$1/therm — dual-fuel often beats either single system. Run the calculator with both system types.",
    },
    {
      q: "What's the federal tax credit for a new HVAC in 2026?",
      a: "None for new installations. The 25C Energy Efficient Home Improvement Credit expired for property placed in service after Dec 31, 2025. Equipment installed by that date may still be claimed on a 2025 return via IRS Form 5695; 2026 projects should budget on gross cost minus state, utility, or HEEHRA incentives only.",
    },
    {
      q: "Do I need a permit to replace my HVAC?",
      a: "Yes — like-for-like swaps are usually a single mechanical permit ($150–$450). Changing system type (gas to heat pump) often adds an electrical permit ($80–$280) for the 240V whip + breaker. New ductwork or condenser-pad relocation may require a building permit too. Total permit + inspection fees usually land $200–$700.",
    },
    {
      q: "How long does a new HVAC system last?",
      a: "Central AC condenser: 12–18 years. Gas furnace: 18–25 years (heat exchanger is the limiter). Heat pump (air-source): 12–16 years. Heat pump (cold-climate / variable-speed inverter): 15–20 years. Mini-split ductless: 15–22 years. Ductwork itself lasts 30–50+ years if maintained — most HVAC 'replacements' are equipment-only, ducts stay.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "System Type",
      question: "What HVAC system are you installing?",
      options: [
        { value: "central_ac_furnace", label: "Central AC + gas furnace", hint: "Traditional split system — most common in older US homes" },
        { value: "heat_pump", label: "Heat pump (all-electric)", hint: "Single system heats + cools — HEEHRA/state rebates may apply" },
        { value: "mini_split", label: "Mini-split (ductless heat pump)", hint: "Per-zone, no ductwork — best for additions / older homes" },
        { value: "dual_fuel", label: "Dual fuel (heat pump + furnace)", hint: "Hybrid — heat pump for milder days, furnace below ~5°F" },
        { value: "ac_only", label: "Central AC only", hint: "Replacing AC, keeping existing furnace" },
      ],
    },
    {
      key: "size",
      title: "Home Size",
      question: "How large is the conditioned area?",
      options: [
        { value: "small", label: "Under 1,500 sqft", hint: "1–2 zones, 2–3 ton system" },
        { value: "medium", label: "1,500–2,500 sqft", hint: "Most US single-family — 3–4 ton system" },
        { value: "large", label: "Over 2,500 sqft", hint: "Multi-zone, 4–5+ ton system" },
      ],
    },
    {
      key: "quality",
      title: "Quality Level",
      question: "Single-stage / two-stage / variable-speed?",
      options: [
        { value: "budget", label: "Budget (single-stage)", hint: "SEER2 14-15, base-tier brands" },
        { value: "mid", label: "Mid-range (two-stage)", hint: "SEER2 16-17, Carrier/Trane/Lennox mid lines" },
        { value: "high", label: "High-end (variable-speed)", hint: "SEER2 18+, inverter / cold-climate spec" },
      ],
    },
  ],
  // 2026 installed pricing pre-multiplier.
  baseRanges: ({ scope, size }) => {
    const map = {
      central_ac_furnace: { small: [5500, 9500],  medium: [7500, 13000], large: [10000, 17000] },
      heat_pump:          { small: [8000, 14000], medium: [11000, 18500], large: [15000, 24000] },
      mini_split:         { small: [4500, 7500],  medium: [7000, 12500], large: [10500, 17500] },
      dual_fuel:          { small: [9000, 14500], medium: [13000, 20500], large: [17000, 27500] },
      ac_only:            { small: [3500, 5800],  medium: [4800, 8000],  large: [6500, 11000] },
    };
    return map[scope]?.[size] || null;
  },
};

const FURNACE = {
  slug: "furnace",
  path: "/furnace-cost-calculator",
  statePathBase: "/furnace-cost",
  name: "Heating & Furnace",
  shortName: "Furnace",
  description: "Estimate furnace + heating-system replacement — gas, electric, oil-to-gas conversion, oil-to-heat-pump, boiler, radiant — with state-adjusted 2026 pricing.",
  image: "/calc-heroes/furnace.jpg",
  pinImage: "/pins/energy-calcs/furnace.jpg",
  icon: "flame",
  energyRebateProjectSlug: "heat-pump", // applies to oil-to-heat-pump conversion path
  timeline: "Like-for-like gas furnace swap: 1 day. Oil-to-gas conversion: 2–3 days (line install + tank removal). Oil-to-heat-pump conversion: 3–5 days (electrical service upgrade often required). Boiler replacement: 2–4 days. Radiant retrofit: 2–4 weeks (depending on scope and finish-floor work).",
  tips: [
    "Oil-to-heat-pump conversion can still stack HEEHRA (income-qualified) with state and utility rebates — but the federal 25C credit expired Dec 31, 2025, so it contributes $0.",
    "Electric resistance furnaces are 3× more expensive to run than heat pumps in most US climates — if you're on electric heat, the heat pump payback is usually under 5 years.",
    "Don't undersize. Manual J load calculation ($150–$400) prevents the #1 furnace failure mode: short-cycling from oversized equipment that runs in 4-minute blasts and stresses the heat exchanger.",
    "If you have a boiler / radiant system, upgrade the boiler — DON'T rip out the radiant. Radiant floor heat is one of the highest-comfort, longest-lifecycle (40–60 yr) systems in residential HVAC.",
  ],
  seo: {
    title: "Furnace Cost 2026 — Gas, Electric, Oil-to-Heat-Pump Conversion",
    description: "Free 2026 furnace replacement calculator. Gas furnace ($4K-$8K), electric ($3K-$6K), oil-to-gas conversion ($7K-$14K), or oil-to-heat-pump conversion with HEEHRA/state rebate overlay.",
  },
  faqs: [
    {
      q: "How much does a new furnace cost in 2026?",
      a: "Gas furnace replacement (80% AFUE): $3,800–$6,200 installed. High-efficiency gas furnace (95%+ AFUE): $5,200–$9,500. Electric furnace: $2,800–$5,500. Oil furnace: $4,500–$7,800 (becoming rare — most homeowners switch fuels at replacement). Boiler (combi gas): $7,500–$14,500. Radiant retrofit: $9–$22/sqft floor area.",
    },
    {
      q: "Is it worth converting from oil to gas or to a heat pump?",
      a: "Oil-to-heat-pump is often the strongest 2026 ROI path: HEEHRA (income-qualified) plus state and utility incentives can still offset a meaningful share of the conversion, and you eliminate the oil tank ($1,500–$3,500 removal cost). The federal 25C credit expired Dec 31, 2025, so it contributes $0. Oil-to-gas only wins if natural gas is already at the curb — running a new service line is $2,000–$6,000 just for the connection.",
    },
    {
      q: "What's the federal tax credit for a new furnace?",
      a: "None for new 2026 installations. The 25C credit expired for property placed in service after Dec 31, 2025. Equipment installed by that date may still be claimed on a 2025 return via IRS Form 5695; new projects should budget on gross cost minus state, utility, or HEEHRA incentives only.",
    },
    {
      q: "Do I need a permit to replace a furnace?",
      a: "Yes — mechanical permit required in all 50 states for furnace replacement. Cost varies $120–$420 depending on jurisdiction. Fuel conversion (oil-to-gas, gas-to-electric) adds a gas-line or electrical-service permit ($80–$320). HOA approval is rarely required for indoor equipment.",
    },
    {
      q: "How long does a furnace last?",
      a: "Standard-efficiency gas furnace: 18–25 years. High-efficiency (condensing) gas: 15–22 years (heat exchanger sensitivity). Electric furnace: 25–35 years (fewer moving parts). Oil furnace: 20–30 years. Boiler: 25–35 years (cast-iron longer). Radiant tubing in slab: 40–60+ years.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "System Type",
      question: "What heating system are you installing?",
      options: [
        { value: "gas_furnace", label: "Gas furnace replacement", hint: "80% or 95% AFUE — most common US heating system" },
        { value: "electric_furnace", label: "Electric furnace", hint: "Resistance-heat (most expensive to run)" },
        { value: "oil_to_gas", label: "Oil-to-gas conversion", hint: "Includes line install + tank removal" },
        { value: "oil_to_heat_pump", label: "Oil-to-heat-pump conversion", hint: "Highest 2026 incentive stack" },
        { value: "boiler", label: "Boiler / radiant", hint: "Hydronic / radiant-floor — common in NE/MW" },
      ],
    },
    {
      key: "size",
      title: "Home Size",
      question: "How large is the heated area?",
      options: [
        { value: "small", label: "Under 1,500 sqft", hint: "60–80 kBTU/h system" },
        { value: "medium", label: "1,500–2,500 sqft", hint: "80–100 kBTU/h system" },
        { value: "large", label: "Over 2,500 sqft", hint: "100–140 kBTU/h system" },
      ],
    },
    {
      key: "quality",
      title: "Quality Level",
      question: "Standard / high-efficiency / premium?",
      options: [
        { value: "budget", label: "Budget", hint: "80% AFUE / base-tier brand" },
        { value: "mid", label: "Mid-range", hint: "92–95% AFUE / modulating" },
        { value: "high", label: "High-end", hint: "98%+ AFUE / variable-speed ECM blower" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      gas_furnace:       { small: [3800, 6200],  medium: [5200, 9500],  large: [7000, 12500] },
      electric_furnace:  { small: [2800, 4500],  medium: [3500, 5500],  large: [4500, 7500] },
      oil_to_gas:        { small: [7000, 11000], medium: [9500, 14500], large: [12500, 19000] },
      oil_to_heat_pump:  { small: [10500, 16000], medium: [13500, 20500], large: [17500, 26500] },
      boiler:            { small: [7500, 11500], medium: [9500, 14500], large: [13000, 19500] },
    };
    return map[scope]?.[size] || null;
  },
};

const INSULATION = {
  slug: "insulation",
  path: "/insulation-cost-calculator",
  statePathBase: "/insulation-cost",
  name: "Home Insulation",
  shortName: "Insulation",
  description: "Estimate home insulation costs — attic, wall, crawlspace, or whole-home weatherization — with state-adjusted 2026 pricing and HEEHRA / utility rebate overlay.",
  image: "/calc-heroes/insulation.jpg",
  pinImage: "/pins/energy-calcs/insulation.jpg",
  icon: "layers",
  energyRebateProjectSlug: "insulation",
  timeline: "Attic blown-in insulation: 4–8 hours, 1 day. Wall insulation (drill + fill or open-wall): 2–4 days depending on wall count. Crawlspace encapsulation + insulation: 2–5 days. Whole-home weatherization (air sealing + attic + crawl): 5–10 working days. Mass Save / utility-rebated jobs often have a 4–6 week scheduling lead time.",
  tips: [
    "Attic-only insulation has the fastest payback in residential energy — typically 3–5 years on heating-and-cooling savings alone, before any state or utility rebate.",
    "The federal 25C insulation credit expired Dec 31, 2025. State and utility rebates (Mass Save covers 75–100% for some households) are the remaining incentive layer.",
    "Blow before you blow — air-seal the attic floor BEFORE adding insulation, otherwise the new R-value is undermined by air leaks. Add $500–$1,500 for proper air sealing.",
    "Get a free home energy audit first — many utilities offer them free. The former $150 federal 25C audit credit expired Dec 31, 2025.",
    "Spray foam beats fiberglass in performance but costs 3–4× more. For most US homes, blown-in cellulose at R-49+ in the attic is the best $/R-value choice.",
  ],
  seo: {
    title: "Home Insulation Cost 2026 — Attic + Wall + Crawlspace",
    description: "Free 2026 home insulation cost calculator — attic blown-in, wall drill-and-fill, crawlspace encapsulation, whole-home weatherization. State-adjusted pricing with HEEHRA + state/utility rebates.",
  },
  faqs: [
    {
      q: "How much does home insulation cost in 2026?",
      a: "Attic blown-in (R-49, 1,500 sqft attic): $1,500–$3,200 installed. Wall drill-and-fill insulation (1,500 sqft): $2,800–$5,500. Spray foam (open-cell, attic): $4,500–$9,500. Crawlspace encapsulation + insulation: $4,800–$11,500. Whole-home weatherization (air seal + attic + crawl): $5,800–$14,500. Mass Save / utility programs often offset 75–100% of these costs.",
    },
    {
      q: "Is attic insulation worth it in 2026?",
      a: "Yes — for the vast majority of US homes built before 2010, attic insulation is the single highest-ROI energy upgrade. Typical 3–5 year payback before incentives, sometimes 1–3 years after utility rebates. Houses in cold states (MN/MI/WI/ME) and hot states (TX/AZ/FL) see the fastest payback because of the energy delta. The federal 25C credit expired Dec 31, 2025.",
    },
    {
      q: "What's the federal tax credit for insulation?",
      a: "None for new 2026 installations. The 25C credit expired for property placed in service after Dec 31, 2025. HEEHRA point-of-sale rebates (up to $1,600 for income-qualified households where live) and utility rebates may still apply.",
    },
    {
      q: "Do I need a permit to add insulation?",
      a: "Attic blown-in: usually NO permit. Wall drill-and-fill in existing walls: usually NO permit. Spray foam (chemical application) requires a permit in 22 states + many cities ($80–$240). Crawlspace encapsulation: permit varies — required when adding a dehumidifier or sealing combustion-appliance air sources. Always confirm with your local building department.",
    },
    {
      q: "How long does insulation last?",
      a: "Blown-in cellulose: 20–30 years (settles 10–15% over time, may need top-up). Fiberglass batt: 25–50 years (degrades from moisture). Spray foam (closed-cell): 60–80+ years. Mineral wool: 60–80+ years. Insulation rarely 'fails' — it's air-sealing and moisture management that fail first.",
    },
  ],
  steps: [
    {
      key: "scope",
      title: "Area to Insulate",
      question: "Where are you adding insulation?",
      options: [
        { value: "attic", label: "Attic only", hint: "Fastest payback — blown-in or batt" },
        { value: "walls", label: "Walls only", hint: "Drill-and-fill into existing walls" },
        { value: "crawlspace", label: "Crawlspace only", hint: "Encapsulation + perimeter foam" },
        { value: "attic_walls", label: "Attic + walls", hint: "Two-area package" },
        { value: "whole_home", label: "Whole-home weatherization", hint: "Air seal + attic + walls + crawl + audit" },
      ],
    },
    {
      key: "size",
      title: "Home Size",
      question: "How large is the area?",
      options: [
        { value: "small", label: "Under 1,500 sqft", hint: "Small attic / under-1500-sqft home" },
        { value: "medium", label: "1,500–2,500 sqft", hint: "Most US single-family" },
        { value: "large", label: "Over 2,500 sqft", hint: "Large or 2-story home" },
      ],
    },
    {
      key: "quality",
      title: "Material Quality",
      question: "What insulation material?",
      options: [
        { value: "budget", label: "Budget (fiberglass)", hint: "R-30–R-38 batt or blown-in fiberglass" },
        { value: "mid", label: "Mid-range (cellulose)", hint: "R-49 blown-in cellulose — best $/R" },
        { value: "high", label: "High-end (spray foam)", hint: "Closed-cell or open-cell spray foam" },
      ],
    },
  ],
  baseRanges: ({ scope, size }) => {
    const map = {
      attic:        { small: [1200, 2400],  medium: [1800, 3500],  large: [2600, 4800] },
      walls:        { small: [2200, 4200],  medium: [3200, 6000],  large: [4500, 8500] },
      crawlspace:   { small: [3800, 6500],  medium: [5500, 9500],  large: [7500, 13000] },
      attic_walls:  { small: [3000, 5800],  medium: [4500, 8500],  large: [6500, 12000] },
      whole_home:   { small: [5500, 10500], medium: [8500, 15500], large: [12500, 22500] },
    };
    return map[scope]?.[size] || null;
  },
};

export const CALCULATORS = {
  bathroom: BATHROOM,
  kitchen: KITCHEN,
  flooring: FLOORING,
  roofing: ROOFING,
  deck: DECK,
  basement: BASEMENT,
  windows: WINDOWS,
  solar: SOLAR,
  fence: FENCE,
  pool: POOL,
  painting: PAINTING,
  hardscape: HARDSCAPE,
  landscaping: LANDSCAPING,
  door: DOOR,
  hvac: HVAC,
  furnace: FURNACE,
  insulation: INSULATION,
};

export const CALCULATOR_LIST = [BATHROOM, KITCHEN, FLOORING, ROOFING, DECK, BASEMENT, WINDOWS, SOLAR, FENCE, POOL, PAINTING, HARDSCAPE, LANDSCAPING, DOOR, HVAC, FURNACE, INSULATION];

export function getCalculator(slug) {
  return CALCULATORS[slug];
}

/**
 * Compute a structured cost estimate.
 * Subtotal range = base * stateMultiplier * qualityMultiplier
 * Breakdown lines split the subtotal (labor/materials/permits) and add a 10% contingency on top.
 * Total = subtotal + contingency.
 */
export function computeEstimate(calculator, values) {
  const { scope, size, quality, state } = values;
  const base = calculator.baseRanges({ scope, size });
  if (!base) return null;
  const stateMult = getStateMultiplier(state);
  const qualityMult = QUALITY_MULTIPLIERS[quality] || 1.0;

  const subLow = base[0] * stateMult * qualityMult;
  const subHigh = base[1] * stateMult * qualityMult;

  const buildLine = (pct) => ({
    low: Math.round(subLow * pct),
    high: Math.round(subHigh * pct),
  });
  const labor = buildLine(COST_SPLIT.labor);
  const materials = buildLine(COST_SPLIT.materials);
  const permits = buildLine(COST_SPLIT.permits);
  const contingency = {
    low: Math.round(subLow * CONTINGENCY),
    high: Math.round(subHigh * CONTINGENCY),
  };
  const total = {
    low: Math.round(subLow + contingency.low),
    high: Math.round(subHigh + contingency.high),
  };

  return {
    breakdown: { labor, materials, permits, contingency },
    total,
    factors: {
      stateMultiplier: stateMult,
      qualityMultiplier: qualityMult,
      stateName: getStateName(state),
    },
  };
}

export function formatUSD(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

/** Build a state-specific landing page URL for a calculator + state slug. */
export function getStateLandingUrl(calculator, stateSlug) {
  return `${calculator.statePathBase}-${stateSlug}`;
}

/** Compute totals for all three quality tiers (Budget / Mid / High) using same scope/size/state. */
export function computeComparison(calculator, values) {
  return ["budget", "mid", "high"].map((quality) => {
    const r = computeEstimate(calculator, { ...values, quality });
    return { quality, total: r ? r.total : null };
  });
}
